<div class="ngtt-container" [class.ngtt-auto-row-height]="enableAutoRowHeight()" [style]="columnSizingVars()">
  <!-- Toolbar -->
  @if (showToolbar()) {
    <div class="ngtt-toolbar">
      <!-- Global Filter -->
      @if (showGlobalFilter() && enableGlobalFilter()) {
        <div class="ngtt-global-filter">
          <input
            type="search"
            [placeholder]="mergedLabels().searchPlaceholder"
            [value]="globalFilter()"
            (input)="globalFilter.set($any($event.target).value)"
            class="ngtt-filter-input"
          />
        </div>
      }

      <!-- Column Visibility Toggle -->
      @if (showColumnVisibility() && enableColumnVisibility()) {
        <div class="ngtt-column-visibility">
          <button class="ngtt-btn-secondary" type="button" title="Toggle columns">
            {{ mergedLabels().columnsButton }}
          </button>
          <div class="ngtt-column-menu">
            @for (column of table.getAllLeafColumns(); track column.id) {
              @if (column.getCanHide()) {
                <label class="ngtt-column-toggle">
                  <input
                    type="checkbox"
                    [checked]="column.getIsVisible()"
                    (change)="column.toggleVisibility()"
                  />
                  <span>{{ column.columnDef.header }}</span>
                </label>
              }
            }
          </div>
        </div>
      }

      <!-- Reset Filters Button -->
      @if (enableFiltering() || enableGlobalFilter()) {
        <button class="ngtt-btn-secondary" type="button" (click)="resetFilters()">
          {{ mergedLabels().clearFilters }}
        </button>
      }

      <!-- Reset Sorting Button -->
      @if (enableSorting()) {
        <button class="ngtt-btn-secondary" type="button" (click)="resetSorting()">
          {{ mergedLabels().clearSorting }}
        </button>
      }

      <!-- Reset Grouping Button -->
      @if (enableGrouping() && grouping().length > 0) {
        <button class="ngtt-btn-secondary" type="button" (click)="resetGrouping()">
          {{ mergedLabels().clearGrouping }}
        </button>
      }

      <!-- Reset Column Pinning Button -->
      @if (enableColumnPinning() && (columnPinning().left?.length || columnPinning().right?.length)) {
        <button class="ngtt-btn-secondary" type="button" (click)="resetColumnPinning()">
          {{ mergedLabels().clearColumnPinning }}
        </button>
      }

      <!-- Reset Row Pinning Button -->
      @if (enableRowPinning() && (rowPinning().top?.length || rowPinning().bottom?.length)) {
        <button class="ngtt-btn-secondary" type="button" (click)="resetRowPinning()">
          {{ mergedLabels().clearRowPinning }}
        </button>
      }

      <!-- Grouping Indicator -->
      @if (enableGrouping() && grouping().length > 0) {
        <div class="ngtt-grouping-indicator">
          <strong>{{ mergedLabels().groupedBy }}</strong>
          @for (columnId of grouping(); track columnId; let i = $index) {
            @let col = table.getColumn(columnId);
            <span class="ngtt-group-badge">
              {{ col?.columnDef?.header || columnId }}
              @if (i < grouping().length - 1) {
                <span>,</span>
              }
            </span>
          }
        </div>
      }
    </div>
  }

  <!-- Table: Row-by-Row Layout -->
  <div class="ngtt-wrapper" #tableWrapper>
    @if (rows().length === 0) {
      <div class="ngtt-no-data">{{ mergedLabels().noDataAvailable }}</div>
    } @else {
      <div class="ngtt-grid">
        <!-- Header Row -->
        <div class="ngtt-header-row">
          <!-- Auto Select Column Header (pinned at start) -->
          @if (enableRowSelection()) {
            <div
              class="ngtt-header-cell-wrapper ngtt-select-column"
              [class.ngtt-pinned-left]="selectColumnPosition() === 'left'"
              [class.ngtt-pinned-right]="selectColumnPosition() === 'right'"
              [style.width]="'48px'"
              [style.min-width]="'48px'"
              [style.left]="selectColumnPosition() === 'left' ? '0px' : null"
              [style.right]="selectColumnPosition() === 'right' ? '0px' : null"
            >
              <div class="ngtt-header-cell">
                @if (enableMultiRowSelection()) {
                  <input
                    type="checkbox"
                    [checked]="table.getIsAllRowsSelected()"
                    [indeterminate]="table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()"
                    (change)="table.toggleAllRowsSelected()"
                  />
                }
              </div>
            </div>
          }

          @for (column of visibleColumns(); track column.id) {
            @let pinPos = pinnedColumnPositions()[column.id];
            <div
              class="ngtt-header-cell-wrapper"
              [style.width]="'var(--col-' + column.id + '-size)'"
              [style.min-width]="getColumnMinWidth(column)"
              [class.ngtt-pinned-left]="pinPos?.position === 'left'"
              [class.ngtt-pinned-right]="pinPos?.position === 'right'"
              [style.left]="pinPos?.position === 'left' ? pinPos.offset : null"
              [style.right]="pinPos?.position === 'right' ? pinPos.offset : null"
            >
              <div
                class="ngtt-header-cell"
                [class.ngtt-sortable]="column.getCanSort()"
                [class.ngtt-sorted]="column.getIsSorted()"
                [class.ngtt-resizable]="column.getCanResize()"
                [class.ngtt-draggable]="enableColumnOrdering()"
                [class.ngtt-dragging]="draggedColumnId() === column.id"
                [class.ngtt-drop-target]="dropTargetColumnId() === column.id"
                [attr.draggable]="enableColumnOrdering() ? 'true' : 'false'"
                (click)="column.getCanSort() ? handleSort(column, $event) : null"
                (dragstart)="onColumnDragStart($event, column.id)"
                (dragover)="onColumnDragOver($event, column.id)"
                (dragleave)="onColumnDragLeave($event)"
                (drop)="onColumnDrop($event, column.id)"
                (dragend)="onColumnDragEnd($event)"
              >
                <!-- Main Row: Label + Actions -->
                <div class="ngtt-header-main-row">
                  <!-- Label area -->
                  <div class="ngtt-header-label">
                    <ng-container
                      *flexRender="column.columnDef.header; props: getHeaderContext(column); let headerContent"
                    >
                      <span class="ngtt-header-title" [innerHTML]="headerContent"></span>
                    </ng-container>

                    @if (column.getIsSorted()) {
                      <span class="ngtt-sort-indicator">
                        @if (column.getIsSorted() === 'asc') {
                          <span class="ngtt-sort-icon ngtt-icon-sm" [innerHTML]="icons().sortAsc"></span>
                        } @else if (column.getIsSorted() === 'desc') {
                          <span class="ngtt-sort-icon ngtt-icon-sm" [innerHTML]="icons().sortDesc"></span>
                        }
                        @if (column.getSortIndex() > -1 && enableMultiSort()) {
                          <span class="ngtt-sort-index">{{ column.getSortIndex() + 1 }}</span>
                        }
                      </span>
                    }
                  </div>

                  <!-- Actions area (right side) -->
                  <div class="ngtt-header-actions" (click)="$event.stopPropagation()">
                      @if (column.getCanFilter() && enableFiltering()) {
                        <button
                          class="ngtt-btn-header-action ngtt-btn-filter"
                          [class.ngtt-active]="hasColumnFilter(column.id)"
                          (click)="toggleFilterPopover(column.id, $event)"
                          type="button"
                          [title]="hasColumnFilter(column.id) ? 'Filter active - click to edit' : 'Filter this column'"
                        >
                          <span class="ngtt-icon ngtt-icon-sm" [innerHTML]="hasColumnFilter(column.id) ? icons().filterActive : icons().filter"></span>
                        </button>
                      }

                      <button
                        class="ngtt-btn-header-action ngtt-btn-menu"
                        (click)="toggleColumnMenu(column.id, $event)"
                        type="button"
                        [title]="mergedLabels().columnMenuTitle"
                      >
                        <span class="ngtt-icon" [innerHTML]="icons().menu"></span>
                      </button>

                      <!-- Column Menu Dropdown -->
                      @if (columnMenuOpenId() === column.id) {
                        <div class="ngtt-column-menu-dropdown" (click)="$event.stopPropagation()">
                          @if (enableSorting() && column.getCanSort()) {
                            <button class="ngtt-menu-item" (click)="column.toggleSorting(false); closeColumnMenu()">
                              <span class="ngtt-menu-icon" [innerHTML]="icons().sortAsc"></span> {{ mergedLabels().sortAscending }}
                            </button>
                            <button class="ngtt-menu-item" (click)="column.toggleSorting(true); closeColumnMenu()">
                              <span class="ngtt-menu-icon" [innerHTML]="icons().sortDesc"></span> {{ mergedLabels().sortDescending }}
                            </button>
                            @if (enableMultiSort()) {
                              <button class="ngtt-menu-item" (click)="column.toggleSorting(false, true); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().multiSortAsc"></span> {{ mergedLabels().addSortAscending }}
                              </button>
                              <button class="ngtt-menu-item" (click)="column.toggleSorting(true, true); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().multiSortDesc"></span> {{ mergedLabels().addSortDescending }}
                              </button>
                            }
                            @if (column.getIsSorted()) {
                              <button class="ngtt-menu-item" (click)="column.clearSorting(); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().clear"></span> {{ mergedLabels().clearSort }}
                              </button>
                            }
                            <div class="ngtt-menu-divider"></div>
                          }

                          @if (enableGrouping() && column.getCanGroup()) {
                            @if (isColumnGrouped(column.id)) {
                              <button class="ngtt-menu-item" (click)="toggleColumnGrouping(column.id); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().ungroup"></span> {{ mergedLabels().removeGrouping }}
                              </button>
                            } @else {
                              <button class="ngtt-menu-item" (click)="toggleColumnGrouping(column.id); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().groupBy"></span> {{ mergedLabels().groupBy }}
                              </button>
                            }
                            <div class="ngtt-menu-divider"></div>
                          }

                          @if (enableColumnPinning()) {
                            @if (getColumnPinState(column.id) === false) {
                              <button class="ngtt-menu-item" (click)="pinColumnLeft(column.id); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().pinLeft"></span> {{ mergedLabels().pinLeft }}
                              </button>
                              <button class="ngtt-menu-item" (click)="pinColumnRight(column.id); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().pinRight"></span> {{ mergedLabels().pinRight }}
                              </button>
                            } @else {
                              <button class="ngtt-menu-item" (click)="unpinColumn(column.id); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().unpin"></span> {{ mergedLabels().unpin }}
                              </button>
                            }
                            <div class="ngtt-menu-divider"></div>
                          }

                          @if (enableColumnVisibility() && column.getCanHide()) {
                            <button class="ngtt-menu-item" (click)="hideColumn(column.id)">
                              <span class="ngtt-menu-icon" [innerHTML]="icons().hide"></span> {{ mergedLabels().hideColumn }}
                            </button>
                          }

                          @if (enableFiltering() && column.getCanFilter() && hasColumnFilter(column.id)) {
                            <button class="ngtt-menu-item" (click)="clearColumnFilter(column.id)">
                              <span class="ngtt-menu-icon" [innerHTML]="icons().close"></span> {{ mergedLabels().clearFilter }}
                            </button>
                          }

                          <!-- Reset Actions -->
                          @if (hasAnyResetAction()) {
                            <div class="ngtt-menu-divider"></div>
                            <div class="ngtt-menu-section-title">{{ mergedLabels().resetSection }}</div>
                            @if (enableFiltering() && columnFilters().length > 0) {
                              <button class="ngtt-menu-item" (click)="resetFilters(); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().close"></span> {{ mergedLabels().clearAllFilters }}
                              </button>
                            }
                            @if (enableSorting() && sorting().length > 0) {
                              <button class="ngtt-menu-item" (click)="resetSorting(); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().close"></span> {{ mergedLabels().clearAllSorting }}
                              </button>
                            }
                            @if (enableGrouping() && grouping().length > 0) {
                              <button class="ngtt-menu-item" (click)="resetGrouping(); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().close"></span> {{ mergedLabels().clearAllGrouping }}
                              </button>
                            }
                            @if (enableColumnPinning() && (columnPinning().left?.length || columnPinning().right?.length)) {
                              <button class="ngtt-menu-item" (click)="resetColumnPinning(); closeColumnMenu()">
                                <span class="ngtt-menu-icon" [innerHTML]="icons().close"></span> {{ mergedLabels().clearAllPinning }}
                              </button>
                            }
                          }
                        </div>
                      }

                      <!-- Filter Popover -->
                      @if (filterPopoverColumnId() === column.id) {
                        <div class="ngtt-filter-popover" (click)="$event.stopPropagation()">
                          <div class="ngtt-filter-popover-header">
                            <span class="ngtt-filter-popover-title">{{ mergedLabels().filterTitle }}</span>
                            <button
                              class="ngtt-btn-popover-close"
                              (click)="closeFilterPopover()"
                              type="button"
                              [title]="mergedLabels().closeTitle"
                            >
                              <span [innerHTML]="icons().close"></span>
                            </button>
                          </div>
                          <div class="ngtt-filter-popover-body">
                            <label class="ngtt-filter-label">{{ mergedLabels().operatorLabel }}</label>
                            <select
                              class="ngtt-filter-popover-select"
                              [value]="getFilterOperator(column.id)"
                              (change)="setFilterOperator(column.id, $any($event.target).value)"
                            >
                              @for (op of localizedFilterOperators(); track op.value) {
                                <option [value]="op.value">{{ op.label }}</option>
                              }
                            </select>

                            @if (getFilterOperator(column.id) !== 'isEmpty' && getFilterOperator(column.id) !== 'isNotEmpty') {
                              <label class="ngtt-filter-label">{{ mergedLabels().valueLabel }}</label>
                              <input
                                type="text"
                                class="ngtt-filter-popover-input"
                                [value]="getFilterDisplayValue(column)"
                                (input)="applyAdvancedFilter(column.id, $any($event.target).value)"
                                [placeholder]="mergedLabels().filterPlaceholder"
                              />
                            }
                          </div>
                          <div class="ngtt-filter-popover-footer">
                            <button
                              class="ngtt-btn-filter-clear"
                              (click)="clearColumnFilter(column.id); closeFilterPopover()"
                              type="button"
                            >
                              {{ mergedLabels().clearButton }}
                            </button>
                            <button
                              class="ngtt-btn-filter-apply"
                              (click)="closeFilterPopover()"
                              type="button"
                            >
                              {{ mergedLabels().doneButton }}
                            </button>
                          </div>
                        </div>
                      }
                    </div>
                </div>
              </div>

              <!-- Resize handle -->
              @if (column.getCanResize() && enableColumnResizing()) {
                @let header = columnHeaders().get(column.id);
                @if (header) {
                  <div
                    class="ngtt-resize-handle"
                    (mousedown)="header.getResizeHandler()($event)"
                    (touchstart)="header.getResizeHandler()($event)"
                  ></div>
                }
              }
            </div>
          }
        </div>

        <!-- Body Rows -->
        <div class="ngtt-body">
          @for (row of rows(); track row.id) {
            <div
              class="ngtt-row"
              [class.ngtt-row-selected]="row.getIsSelected()"
              [class.ngtt-row-grouped]="row.getIsGrouped()"
              [class.ngtt-row-pinned-top]="getRowPinState(row.id) === 'top'"
              [class.ngtt-row-pinned-bottom]="getRowPinState(row.id) === 'bottom'"
            >
              <!-- Auto Select Cell (pinned at start) -->
              @if (enableRowSelection()) {
                <div
                  class="ngtt-cell ngtt-select-column"
                  [class.ngtt-pinned-left]="selectColumnPosition() === 'left'"
                  [class.ngtt-pinned-right]="selectColumnPosition() === 'right'"
                  [style.width]="'48px'"
                  [style.min-width]="'48px'"
                  [style.left]="selectColumnPosition() === 'left' ? '0px' : null"
                  [style.right]="selectColumnPosition() === 'right' ? '0px' : null"
                >
                  <input
                    type="checkbox"
                    [checked]="row.getIsSelected()"
                    [indeterminate]="row.getIsSomeSelected()"
                    (change)="row.toggleSelected()"
                  />
                </div>
              }

              @for (cell of row.getVisibleCells(); track cell.id) {
                @let pinPos = pinnedColumnPositions()[cell.column.id];
                @let isFirstCol = isFirstDataColumn(cell.column.id);
                <div
                  class="ngtt-cell"
                  [style.width]="'var(--col-' + cell.column.id + '-size)'"
                  [style.min-width]="getColumnMinWidth(cell.column)"
                  [class.ngtt-pinned-left]="pinPos?.position === 'left'"
                  [class.ngtt-pinned-right]="pinPos?.position === 'right'"
                  [style.left]="pinPos?.position === 'left' ? pinPos.offset : null"
                  [style.right]="pinPos?.position === 'right' ? pinPos.offset : null"
                  [class.ngtt-has-depth]="row.depth > 0 && enableExpanding() && isFirstCol"
                  [style.padding-left]="getDepthPadding(row.depth, cell.column.id)"
                >
                  <!-- Row Pinning controls (show in first data column) -->
                  @if (enableRowPinning() && isFirstCol) {
                    <div class="ngtt-row-pin-controls">
                      @switch (getRowPinState(row.id)) {
                        @case ('top') {
                          <button
                            class="ngtt-btn-row-pin ngtt-active"
                            (click)="unpinRow(row.id); $event.stopPropagation()"
                            type="button"
                            [title]="mergedLabels().unpinRowTitle"
                          >
                            <span class="ngtt-pin-icon" [innerHTML]="icons().pinned"></span>
                          </button>
                        }
                        @case ('bottom') {
                          <button
                            class="ngtt-btn-row-pin ngtt-active"
                            (click)="unpinRow(row.id); $event.stopPropagation()"
                            type="button"
                            [title]="mergedLabels().unpinRowTitle"
                          >
                            <span class="ngtt-pin-icon" [innerHTML]="icons().pinned"></span>
                          </button>
                        }
                        @default {
                          <button
                            class="ngtt-btn-row-pin"
                            (click)="pinRowTop(row.id); $event.stopPropagation()"
                            type="button"
                            [title]="mergedLabels().pinToTopTitle"
                          >
                            <span class="ngtt-pin-icon" [innerHTML]="icons().pinTop"></span>
                          </button>
                          <button
                            class="ngtt-btn-row-pin"
                            (click)="pinRowBottom(row.id); $event.stopPropagation()"
                            type="button"
                            [title]="mergedLabels().pinToBottomTitle"
                          >
                            <span class="ngtt-pin-icon" [innerHTML]="icons().pinBottom"></span>
                          </button>
                        }
                      }
                    </div>
                  }

                  @if (cell.column.id === 'expander' && enableExpanding()) {
                    <!-- Expand/collapse button -->
                    @if (row.getCanExpand()) {
                      <button
                        class="ngtt-btn-icon"
                        (click)="row.toggleExpanded()"
                        type="button"
                      >
                        <span [innerHTML]="row.getIsExpanded() ? icons().collapse : icons().expand"></span>
                      </button>
                    }
                  } @else {
                    <!-- Grouped row expand/collapse -->
                    @if (row.getIsGrouped() && cell.getIsGrouped() && enableGrouping()) {
                      <button
                        class="ngtt-btn-group-expand"
                        (click)="row.toggleExpanded()"
                        type="button"
                      >
                        <span [innerHTML]="row.getIsExpanded() ? icons().collapse : icons().expand"></span>
                      </button>
                    }

                    <!-- Regular or aggregated cell content -->
                    @if (row.getIsGrouped() && cell.getIsAggregated()) {
                      <!-- Aggregated cell -->
                      <ng-container
                        *flexRender="cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell; props: cell.getContext(); let cellContent"
                      >
                        <span class="ngtt-aggregated-value" [innerHTML]="cellContent"></span>
                      </ng-container>
                    } @else {
                      <!-- Regular cell content -->
                      <ng-container
                        *flexRender="cell.column.columnDef.cell; props: cell.getContext(); let cellContent"
                      >
                        <span [innerHTML]="cellContent"></span>
                      </ng-container>
                    }
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  </div>

  <!-- Pagination -->
  @if (showPagination() && enablePagination()) {
    <div class="ngtt-pagination">
      <div class="ngtt-pagination-info">
        {{ formatShowingRows(paginationInfo().startRow, paginationInfo().endRow, paginationInfo().totalRows) }}
      </div>

      <div class="ngtt-pagination-controls">
        <button
          class="ngtt-btn-icon"
          (click)="table.firstPage()"
          [disabled]="!paginationInfo().canPreviousPage"
          type="button"
          [title]="mergedLabels().firstPageTitle"
        >
          <span [innerHTML]="icons().firstPage"></span>
        </button>
        <button
          class="ngtt-btn-icon"
          (click)="table.previousPage()"
          [disabled]="!paginationInfo().canPreviousPage"
          type="button"
          [title]="mergedLabels().previousPageTitle"
        >
          <span [innerHTML]="icons().previousPage"></span>
        </button>
        <span class="ngtt-page-info">
          {{ formatPageInfo(paginationInfo().currentPage, paginationInfo().totalPages) }}
        </span>
        <button
          class="ngtt-btn-icon"
          (click)="table.nextPage()"
          [disabled]="!paginationInfo().canNextPage"
          type="button"
          [title]="mergedLabels().nextPageTitle"
        >
          <span [innerHTML]="icons().nextPage"></span>
        </button>
        <button
          class="ngtt-btn-icon"
          (click)="table.lastPage()"
          [disabled]="!paginationInfo().canNextPage"
          type="button"
          [title]="mergedLabels().lastPageTitle"
        >
          <span [innerHTML]="icons().lastPage"></span>
        </button>
      </div>

      <div class="ngtt-page-size-selector">
        <label>
          <span>{{ mergedLabels().rowsPerPage }}</span>
          <select
            [value]="paginationInfo().pageSize"
            (change)="table.setPageSize(+$any($event.target).value)"
          >
            @for (size of pageSizeOptions(); track size) {
              <option [value]="size">{{ size }}</option>
            }
          </select>
        </label>
      </div>
    </div>
  }
</div>
